const API_URL = import.meta.env.VITE_API_URL;

/**
 * Obtiene el token de autenticación desde localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('accessToken');
};

/**
 * Obtiene el pop-up activo actual (endpoint público)
 */
export const getActivePopup = async () => {
  try {
    const response = await fetch(`${API_URL}/popups/active`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 404 || response.status === 204) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return null;
    }

    const text = await response.text();
    if (!text || text.trim() === '' || text.trim() === 'null') {
      return null;
    }

    try {
      const data = JSON.parse(text);
      return data;
    } catch (parseError) {
      console.warn('Error parsing popup response as JSON:', parseError);
      return null;
    }
  } catch (error) {
    if (error.message && error.message.includes('JSON')) {
      return null;
    }
    console.error('Error fetching active popup:', error);
    return null;
  }
};

/**
 * Obtiene todos los pop-ups (requiere autenticación admin)
 */
export const getAllPopups = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al obtener pop-ups' }));
    throw new Error(error.message || 'Error al obtener pop-ups');
  }

  return response.json();
};

/**
 * Crea un nuevo pop-up (requiere autenticación admin)
 */
export const createPopup = async (file, data = {}) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const formData = new FormData();
  formData.append('image', file);
  if (data.title) formData.append('title', data.title);
  if (data.link) formData.append('link', data.link);
  if (data.position !== undefined) formData.append('position', data.position);
  if (data.isActive !== undefined) formData.append('isActive', data.isActive);
  if (data.startDate) formData.append('startDate', data.startDate);

  const response = await fetch(`${API_URL}/popups`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al crear pop-up' }));
    throw new Error(error.message || 'Error al crear pop-up');
  }

  return response.json();
};

/**
 * Actualiza un pop-up (requiere autenticación admin)
 */
export const updatePopup = async (id, data) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/${id}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al actualizar pop-up' }));
    throw new Error(error.message || 'Error al actualizar pop-up');
  }

  return response.json();
};

/**
 * Elimina un pop-up (requiere autenticación admin)
 */
export const deletePopup = async (id) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al eliminar pop-up' }));
    throw new Error(error.message || 'Error al eliminar pop-up');
  }

  return response.json();
};

/**
 * Ejecuta rotación manual de pop-ups (requiere autenticación admin)
 */
export const rotatePopups = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/rotate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al rotar pop-ups' }));
    throw new Error(error.message || 'Error al rotar pop-ups');
  }

  return response.json();
};

/**
 * Reinicia el loop de pop-ups desde el principio (requiere autenticación admin)
 */
export const resetLoop = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/reset-loop`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al reiniciar loop' }));
    throw new Error(error.message || 'Error al reiniciar loop');
  }

  return response.json();
};

/**
 * Pausa el ciclo de rotación automática (requiere autenticación admin)
 */
export const pauseRotation = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/pause-rotation`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al pausar rotación' }));
    throw new Error(error.message || 'Error al pausar rotación');
  }

  return response.json();
};

/**
 * Reanuda el ciclo de rotación automática (requiere autenticación admin)
 */
export const resumeRotation = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/resume-rotation`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al reanudar rotación' }));
    throw new Error(error.message || 'Error al reanudar rotación');
  }

  return response.json();
};

/**
 * Obtiene el estado del ciclo de rotación (requiere autenticación admin)
 */
export const getRotationStatus = async () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/rotation-status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al obtener estado' }));
    throw new Error(error.message || 'Error al obtener estado');
  }

  return response.json();
};

/**
 * Actualiza la duración de rotación en minutos (requiere autenticación admin)
 */
export const updateRotationDuration = async (durationMinutes) => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No hay token de autenticación');
  }

  const response = await fetch(`${API_URL}/popups/rotation-duration`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ durationMinutes }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Error al actualizar duración' }));
    throw new Error(error.message || 'Error al actualizar duración');
  }

  return response.json();
};

